import { useQuery } from "@tanstack/react-query";
import { Application } from "shared/types";
import api from "api/landlord/address";
import { useCallback, useState } from "react";
import { DecisionAction } from "shared/types/application";
import { Template } from "types/landlord/verificationTemplate";
import { Views, FormData, FormDataErrors } from "./types";
import {
  AdverseActionLetterType,
  UnlistUnitTiming
} from "api/landlord/templates";
import { RuleSetDecision } from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/types";
import { Reasons } from "types/application";
import { conditionKeysMap } from "./constants";
import { useSettingsController } from "views/settings-integration/controller";
import { getFlow } from "./utils";

export const useMakeDecisionController = (applicationId: string) => {
  const [view, setView] = useState<Views>(Views.MakeDecision);
  const [formData, setFormData] = useState<FormData>({
    decisionNotes: "",
    unlistUnit: true,
    disallowApplications: true,
    sendNotifications: false,
    addApplicantsToPMS: false,
    conditions: [],
    residencyConditions: [],
    additionalCondition: "",
    additionalEmailText: "",
    additionalResidencyCondition: "",
    increasedMonthlyRent: ""
  });
  const [formDataErrors, setFormDataErrors] = useState<FormDataErrors>({
    increasedMonthlyRent: ""
  });

  const { currentSettings } = useSettingsController();
  const { pms: pmsSettings } = currentSettings;

  const updateFormData = useCallback(
    (updates: Partial<FormData>) => {
      setFormData((prev) => ({
        ...prev,
        ...updates
      }));

      // Clear corresponding errors for updated keys
      const clearedErrors: Partial<FormDataErrors> = {};
      for (const key of Object.keys(updates) as (keyof FormData)[])
        if (formDataErrors[key]) clearedErrors[key] = "";

      setFormDataErrors((prev) => ({
        ...prev,
        ...clearedErrors
      }));
    },
    [formDataErrors]
  );

  const applicationMeta = useQuery<
    Application & {
      verification_template: Template;
      score: number;
      rule_reasons?: Reasons[];
    }
  >({
    queryKey: ["APPLICATION", applicationId],
    queryFn: () =>
      api.getApplicationById(applicationId || "").then((response) => {
        const res = response as unknown as Application & {
          verification_template: Template;
          score: number;
          rule_reasons?: Reasons[];
        };

        setFormData((prev) => ({
          ...prev,
          conditions: res.rule_reasons
            ? getTransformedConditions(res.rule_reasons, res.recommendation)
            : [],
          decision: res.recommendation as DecisionAction,
          unlistUnit:
            res.verification_template.automatically_unlist_unit_timing ===
            UnlistUnitTiming.ON_APPROVE,
          disallowApplications:
            res.verification_template.automatically_unlist_unit_timing ===
            UnlistUnitTiming.ON_APPROVE,
          sendNotifications: res.verification_template.notify_applicants,
          addApplicantsToPMS:
            res.property?.pms_id || res.property?.external_id
              ? pmsSettings[`pms_prospect_creation_on_${res.recommendation}`] ||
                prev.addApplicantsToPMS
              : false
        }));
        return res;
      }),
    enabled: !!applicationId
  });

  const flow = getFlow(
    formData.decision,
    applicationMeta.data?.verification_template,
    formData.sendNotifications
  );

  const setNextView = () => {
    const currentViewIndex = flow.indexOf(view);
    if (currentViewIndex == 0)
      resetConditions(
        applicationMeta.data?.rule_reasons,
        applicationMeta.data?.recommendation
      );
    setView(flow[currentViewIndex + 1]);
  };

  const setPrevView = () => {
    const currentViewIndex = flow.indexOf(view);
    setView(flow[currentViewIndex - 1]);
  };

  const validateFormData = () => {
    if (
      formData.residencyConditions?.includes("increased_monthly_rent") &&
      !formData.increasedMonthlyRent
    ) {
      setFormDataErrors((prev) => ({
        ...prev,
        increasedMonthlyRent: "Please enter an amount"
      }));
      return false;
    }

    return true;
  };

  const getTransformedConditions = (
    ruleReasons: Reasons[],
    recommendation: DecisionAction | undefined
  ) => {
    if (
      applicationMeta.data?.recommendation !== formData.decision ||
      applicationMeta.data?.verification_template.adverse_action_letter_type ===
        AdverseActionLetterType.Washington
    )
      return [];

    const ruleSetType =
      recommendation === DecisionAction.ConditionalApprove
        ? RuleSetDecision.Approve
        : RuleSetDecision.Decline;

    return ruleReasons.reduce<string[]>((result, ruleSet) => {
      if (ruleSet.rule_set_type !== ruleSetType) return result;

      return result.concat(
        ruleSet.rules.reduce<string[]>((fields, rule) => {
          if (
            rule.result ===
            (recommendation !== DecisionAction.ConditionalApprove)
          ) {
            const condition = conditionKeysMap[rule.field_name];
            if (condition) fields.push(condition);
          }

          return fields;
        }, [])
      );
    }, []);
  };

  const resetConditions = (
    ruleReasons: Reasons[] | undefined,
    recommendation: DecisionAction | undefined
  ) => {
    if (ruleReasons)
      setFormData((prev) => ({
        ...prev,
        conditions: getTransformedConditions(ruleReasons, recommendation)
      }));
  };

  const onNextView = () => {
    if (!validateFormData()) return;
    setNextView();
  };

  return {
    applicationMeta,
    formData,
    updateFormData,
    onNextView: onNextView,
    setPrevView,
    view,
    flow,
    resetConditions,
    validateFormData,
    formDataErrors
  };
};
